// Penyandi barcode CODE 128 (subset B) — dipakai tiket antrian.
//
// KENAPA DITULIS SENDIRI, BUKAN PUSTAKA:
// tidak ada pustaka pembuat barcode di package.json (yang ada `@zxing/library`,
// itu PEMBACA), dan menambah dependensi berarti `yarn install` di setiap mesin
// + di pipeline deploy. Penyandi Code128B itu satu tabel pola + satu checksum;
// lebih kecil daripada ongkos dependensinya.
//
// KENAPA SUBSET B, BUKAN EAN-13:
// kode antrian memuat HURUF (nama cabang) — EAN-13 hanya angka. Subset B
// mencakup ASCII 32..126, jadi seluruh kode `ANT-<CABANG>-<YYYYMMDD>-<NNN>`
// masuk apa adanya tanpa penyandian tambahan.
//
// Hasil penyandi ini SUDAH DIVERIFIKASI dengan membaca baliknya memakai
// `@zxing/library` (dekoder yang sama yang dipakai `components/hris/
// BarcodeScanner.jsx` di lapangan), bukan dengan membaca ulang tabelnya.

// Tabel pola resmi Code 128 — 107 baris, tiap angka = lebar satu elemen dalam
// modul, berselang-seling bar/spasi mulai dari BAR. Indeks 0..102 = nilai data,
// 103/104/105 = START A/B/C, 106 = STOP (7 elemen, 13 modul).
const POLA = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312",
  "132212", "221213", "221312", "231212", "112232", "122132", "122231", "113222",
  "123122", "123221", "223211", "221132", "221231", "213212", "223112", "312131",
  "311222", "321122", "321221", "312212", "322112", "322211", "212123", "212321",
  "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121",
  "313121", "211331", "231131", "213113", "213311", "213131", "311123", "311321",
  "331121", "312113", "312311", "332111", "314111", "221411", "431111", "111224",
  "111422", "121124", "121421", "141122", "141221", "112214", "112412", "122114",
  "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112",
  "421211", "212141", "214121", "412121", "111143", "111341", "131141", "114113",
  "114311", "411113", "411311", "113141", "114131", "311141", "411131", "211412",
  "211214", "211232", "2331112",
];

const START_B = 104;
const STOP = 106;

/** Karakter yang bisa disandikan subset B: ASCII 32..126.
 *  Dipakai pemanggil untuk memutuskan menampilkan barcode atau tidak —
 *  menggambar barcode dari kode yang tidak tersandikan menghasilkan gambar
 *  yang TERLIHAT benar tapi terbaca lain (atau tidak terbaca) oleh pemindai,
 *  dan itu jauh lebih buruk daripada tidak ada barcode sama sekali. */
export function bisaDisandikan(teks) {
  if (typeof teks !== "string" || teks.length === 0) return false;
  for (let i = 0; i < teks.length; i += 1) {
    const c = teks.charCodeAt(i);
    if (c < 32 || c > 126) return false;
  }
  return true;
}

/**
 * Sandikan `teks` jadi deretan modul 0/1 (1 = bar hitam).
 * Mengembalikan array angka, atau null kalau teksnya tidak bisa disandikan.
 *
 * Zona sunyi (quiet zone) TIDAK ikut di sini — itu urusan yang menggambar,
 * karena lebarnya bergantung skala piksel/mm yang dipakai.
 */
export function modulCode128(teks) {
  if (!bisaDisandikan(teks)) return null;

  const nilai = [START_B];
  for (let i = 0; i < teks.length; i += 1) {
    nilai.push(teks.charCodeAt(i) - 32);
  }
  // Checksum Code 128: nilai START + Σ (posisi × nilai), posisi mulai 1.
  let jumlah = START_B;
  for (let i = 1; i < nilai.length; i += 1) {
    jumlah += i * nilai[i];
  }
  nilai.push(jumlah % 103);
  nilai.push(STOP);

  const modul = [];
  nilai.forEach((v) => {
    const pola = POLA[v];
    for (let i = 0; i < pola.length; i += 1) {
      const lebar = Number(pola[i]);
      const bar = i % 2 === 0; // elemen genap = bar, ganjil = spasi
      for (let n = 0; n < lebar; n += 1) modul.push(bar ? 1 : 0);
    }
  });
  return modul;
}

/**
 * Bentuk deretan modul jadi daftar batang siap gambar:
 * `[{ x, lebar }]` dalam satuan modul (bukan piksel) — batang yang bersebelahan
 * digabung supaya jumlah elemen SVG-nya sepersekian dari jumlah modul.
 */
export function batangCode128(teks) {
  const modul = modulCode128(teks);
  if (!modul) return null;
  const batang = [];
  let i = 0;
  while (i < modul.length) {
    if (modul[i] === 1) {
      let j = i;
      while (j < modul.length && modul[j] === 1) j += 1;
      batang.push({ x: i, lebar: j - i });
      i = j;
    } else {
      i += 1;
    }
  }
  return { batang, totalModul: modul.length };
}
